import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { getUsers } from "../../services/clients";
import {
  deleteAppDataRow,
  getUserAppData,
  saveUserAppData,
  updateAppDataRow,
} from "../../services/appData";
import { summarizeAppData } from "../../utils/appData";
import * as config from "../../config.js";
import { appDataRowEndpoint, sortRows } from "../../utils/appData";
import JsonTree from "./JsonTree";
import { SortHeader } from "./Sortable";
import RowEndpoint from "./RowEndpoint";
import Pager from "./Pager";
import DeleteRowDialog from "./DeleteRowDialog";
import BulkDeleteDialog from "./BulkDeleteDialog";
import { rowKey, useRowSelection } from "./selection";
import AppDataRowDialog from "./AppDataRowDialog";
import {
  Actions,
  Button,
  Card,
  Chip,
  DataRow,
  ErrorBanner,
  Field,
  HeadRow,
  Input,
  Label,
  Mono,
  Muted,
  Notice,
  Row,
  Small,
  Table,
  Toolbar,
  Truncate,
  Wrapper,
} from "./AppDataStyles";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const userLabel = (user) =>
  [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.email || user?.public_id;

/**
 * One user's app data: their profile, their rows, and (optionally) the client's global rows.
 */
export const UserAppDataPanel = ({ clientId = null, usersClientId = null }) => {
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [data, setData] = useState({ user: null, records: [] });
  const [includeGlobal, setIncludeGlobal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsPagination, setRowsPagination] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!usersClientId) {
      setUsers([]);
      return undefined;
    }

    getUsers(usersClientId)
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : list?.users ?? [];
        setUsers(rows);
        setUsersError(null);
        setSelectedUserId((current) => current ?? rows[0]?.public_id ?? null);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setUsers([]);
        setUsersError(loadError?.message || "Could not load the users for this client.");
      });

    return () => {
      cancelled = true;
    };
  }, [usersClientId]);

  const load = React.useCallback(async () => {
    if (!selectedUserId) {
      setData({ user: null, records: [] });
      return;
    }

    setLoading(true);
    setError(null);

    const result = await getUserAppData(selectedUserId, {
      clientId,
      includeGlobal,
      page: debouncedFilter ? 1 : rowsPage,
      pageSize: debouncedFilter ? 500 : 50,
      sort: sortKey,
      direction: sortDir,
    });

    setLoading(false);

    if (result?.success === false) {
      setData({ user: null, records: [] });
      setError(result.message || "Could not load this user's app data.");
      return;
    }

    setData({ user: result.user ?? null, records: sortRows(result.records ?? [], sortKey, sortDir) });
    setRowsPagination(result.pagination ?? null);
    setSelectedRow(null);
  }, [selectedUserId, clientId, includeGlobal, rowsPage, debouncedFilter, sortKey, sortDir]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedFilter(filter.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [filter]);

  useEffect(() => {
    setRowsPage(1);
  }, [selectedUserId, includeGlobal, debouncedFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredUsers = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!text) return users;

    return users.filter((user) =>
      `${user.firstname ?? ""} ${user.lastname ?? ""} ${user.email ?? ""} ${user.public_id ?? ""}`
        .toLowerCase()
        .includes(text)
    );
  }, [users, search]);

  const term = filter.trim().toLowerCase();

  const filteredRecords = data.records.filter(
    (row) =>
      !term ||
      [row.name, row.key, row.data_type]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
  );

  const visibleRecords = filteredRecords;
  const selection = useRowSelection(visibleRecords);

  // Global rows shown via "include global" are read-only; only user rows can be deleted here.
  const deletableSelection = selection.rows.filter((row) => (row.scope ?? "user") !== "global");

  const bulkDelete = async (force) => {
    if (deletableSelection.length === 0) return;

    setBulkBusy(true);
    setBulkError(null);

    let failed = 0;
    let lastError = null;

    for (const row of deletableSelection) {
      // eslint-disable-next-line no-await-in-loop
      const result = await deleteAppDataRow(row.public_id, { force, clientId });

      if (result?.success === false) {
        failed += 1;
        lastError = result.message;
      }
    }

    setBulkBusy(false);
    await load();

    if (failed > 0) {
      setBulkError(`${failed} of ${deletableSelection.length} failed${lastError ? `: ${lastError}` : ""}.`);
      return;
    }

    setBulkOpen(false);
    selection.clear();
    setNotice(`${deletableSelection.length} row${deletableSelection.length === 1 ? "" : "s"} deleted.`);
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "updated_at" ? "desc" : "asc");
    }

    setRowsPage(1);
  };

  const selectedUser = data.user ?? users.find((user) => user.public_id === selectedUserId) ?? null;

  const save = async (payload) => {
    setSaving(true);
    setDialogError(null);

    const result = await saveUserAppData(selectedUserId, payload, { clientId });

    setSaving(false);

    if (result?.success === false) {
      setDialogError(
        result?.status === 409
          ? "A row with that name and key already exists (create-only mode)."
          : result.message || "Could not save the row."
      );
      return;
    }

    setDialog(null);
    setNotice("Row saved.");
    await load();
  };

  const update = async (payload) => {
    setSaving(true);
    setDialogError(null);

    const result = await updateAppDataRow(selectedRow.public_id, payload, { clientId });

    setSaving(false);

    if (result?.success === false) {
      setDialogError(result.message || "Could not update the row.");
      return;
    }

    setDialog(null);
    setNotice("Row updated.");
    await load();
  };

  const remove = async (row, force = false) => {
    if (!row) return;

    setDeleting(true);
    setDeleteError(null);

    const result = await deleteAppDataRow(row.public_id, { force, clientId });

    setDeleting(false);

    if (result?.success === false) {
      setDeleteError(result.message || "Could not delete the row.");
      return;
    }

    setPendingDelete(null);
    setSelectedRow(null);
    setNotice("Row deleted.");
    await load();
  };

  return (
    <Wrapper>
      <Toolbar>
        <Field style={{ maxWidth: "320px" }}>
          <Label>User</Label>
          <Input
            value={search}
            placeholder="Search by name, email or id…"
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>

        <Field style={{ maxWidth: "320px" }}>
          <Label>Select user</Label>
          <select
            value={selectedUserId ?? ""}
            onChange={(event) => setSelectedUserId(event.target.value)}
            style={{
              width: "100%",
              padding: "6px 9px",
              fontSize: "12.5px",
              color: "var(--main-font-color)",
              backgroundColor: "var(--main-bg)",
              border: "1px solid var(--main-border-color)",
              borderRadius: "6px",
            }}
          >
            {filteredUsers.length === 0 ? <option value="">No users</option> : null}
            {filteredUsers.map((user) => (
              <option key={user.public_id} value={user.public_id}>
                {userLabel(user)}
              </option>
            ))}
          </select>
        </Field>

        <Field style={{ maxWidth: "260px" }}>
          <Label>Filter rows</Label>
          <Input
            value={filter}
            placeholder="name, key or data type…"
            onChange={(event) => setFilter(event.target.value)}
          />
        </Field>

        <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12.5px", marginTop: "18px" }}>
          <input type="checkbox" checked={includeGlobal} onChange={(event) => setIncludeGlobal(event.target.checked)} />
          Include global data
        </label>

        {deletableSelection.length > 0 ? (
          <>
            <Small
              $danger
              type="button"
              onClick={() => {
                setBulkOpen(true);
                setBulkError(null);
              }}
            >
              <Icon icon="lucide:trash-can-outline" /> Delete selected ({deletableSelection.length})
            </Small>
            <Small type="button" onClick={selection.clear}>
              Clear
            </Small>
          </>
        ) : null}

        <span style={{ flex: 1 }} />

        <Button
          type="button"
          disabled={!selectedUserId}
          onClick={() => {
            setDialog({ mode: "create", row: null });
            setDialogError(null);
          }}
        >
          <Icon icon="lucide:plus" /> New user row
        </Button>
      </Toolbar>

      {usersError ? <ErrorBanner>{usersError}</ErrorBanner> : null}
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {notice ? <Notice>{notice}</Notice> : null}

      {selectedUser ? (
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <Icon icon="lucide:user" />
            <strong style={{ fontSize: "14px" }}>{userLabel(selectedUser)}</strong>
            <span style={{ fontSize: "12.5px", color: "#a0a6b1" }}>{selectedUser.email ?? "—"}</span>
            <Mono>{selectedUser.public_id}</Mono>
          </div>
        </Card>
      ) : null}

      <Table>
        <HeadRow style={{ gridTemplateColumns: "30px 1.1fr 0.8fr 70px 1.4fr 1.1fr auto" }}>
          <input
            type="checkbox"
            checked={selection.allSelected}
            onChange={selection.toggleAll}
            title="Select all rows on this page"
            style={{ cursor: "pointer" }}
          />
          <SortHeader label="Name" column="name" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
          <SortHeader label="Key" column="key" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
          <span>Scope</span>
          <span>Data</span>
          <SortHeader label="Updated" column="updated_at" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
          <span />
        </HeadRow>

        {loading ? (
          <Row style={{ gridTemplateColumns: "1fr", cursor: "default" }}>
            <Muted>Loading app data…</Muted>
          </Row>
        ) : visibleRecords.length === 0 ? (
          <Row style={{ gridTemplateColumns: "1fr", cursor: "default" }}>
            <Muted>{term ? "No rows match the filter." : "No app data for this user."}</Muted>
          </Row>
        ) : (
          visibleRecords.map((row) => (
            <React.Fragment key={row.public_id ?? `${row.name}/${row.key}`}>
            <DataRow
              $active={selectedRow?.public_id === row.public_id}
              style={{ gridTemplateColumns: "30px 1.1fr 0.8fr 70px 1.4fr 1.1fr auto" }}
              onClick={() => {
                setSelectedRow((current) =>
                  current?.public_id === row.public_id ? null : row
                );
                setPendingDelete(null);
                setDeleteError(null);
              }}
            >
              <span onClick={(event) => event.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selection.isSelected(rowKey(row))}
                  onChange={() => selection.toggle(rowKey(row))}
                  style={{ cursor: "pointer" }}
                />
              </span>
              <Truncate>{row.name}</Truncate>
              <Mono>{row.key}</Mono>
              <span>
                <Chip $tone={row.scope === "global" ? "#3498db" : "#2ecc71"}>{row.scope ?? "user"}</Chip>
              </span>
              <Truncate style={{ color: "#a0a6b1" }}>{summarizeAppData(row.data)}</Truncate>
              <span>{formatDate(row.updated_at)}</span>

              <Actions>
                {row.scope === "global" ? null : (
                  <>
                    <Small
                      type="button"
                      title="Edit this row"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDialog({ mode: "edit", row });
                        setDialogError(null);
                      }}
                    >
                      <Icon icon="lucide:pencil" />
                    </Small>
                    <Small
                      $danger
                      type="button"
                      title="Delete this row"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPendingDelete(row);
                        setDeleteError(null);
                      }}
                    >
                      <Icon icon="lucide:trash-can-outline" />
                    </Small>
                  </>
                )}
              </Actions>
            </DataRow>

            {selectedRow?.public_id === row.public_id ? (
              <div
                style={{
                  padding: "10px 12px 14px",
                  borderBottom: "1px solid var(--content-item-border-color)",
                  backgroundColor: "var(--main-bg)",
                }}
              >
                <RowEndpoint
                  url={appDataRowEndpoint({
                    baseUrl: config.REST_SERVER_URL,
                    scope: row.scope ?? "user",
                    name: row.name,
                    key: row.key,
                    publicId: row.public_id,
                    clientId,
                  })}
                />

                <div style={{ marginTop: "10px" }}>
                  <JsonTree value={row.data} />
                </div>
              </div>
            ) : null}
            </React.Fragment>
          ))
        )}
      </Table>

      <Pager pagination={rowsPagination} onPage={(page) => setRowsPage(page)} loading={loading} />

      <BulkDeleteDialog
        show={bulkOpen}
        count={deletableSelection.length}
        hasLocked={deletableSelection.some((row) => row.locked)}
        busy={bulkBusy}
        error={bulkError}
        onClose={() => setBulkOpen(false)}
        onConfirm={bulkDelete}
      />

      <DeleteRowDialog
        show={Boolean(pendingDelete)}
        row={pendingDelete}
        scope={pendingDelete?.scope ?? "user"}
        busy={deleting}
        error={deleteError}
        onClose={() => setPendingDelete(null)}
        onConfirm={(force) => remove(pendingDelete, force)}
      />

      <AppDataRowDialog
        show={Boolean(dialog)}
        mode={dialog?.mode ?? "create"}
        row={dialog?.row ?? null}
        scope="user"
        userLabel={selectedUser ? userLabel(selectedUser) : null}
        clientId={clientId}
        baseUrl={config.REST_SERVER_URL}
        saving={saving}
        error={dialogError}
        onClose={() => setDialog(null)}
        onSave={dialog?.mode === "edit" ? update : save}
      />
    </Wrapper>
  );
};

export default UserAppDataPanel;
